import { api, signIn as storeCredentials } from "@/shared/api";
import { rememberTenantId } from "./tenant";

/**
 * Sign in with Sarva — backend D315 (Sarva Core C-4, C-15, C-17).
 *
 * Sarva Core is the one sign-in for every Sarva product. Its session lives in an `HttpOnly`
 * cookie on Core's own API host; every Sarva app is under `*.sarvahealth.ai`, so a fetch from
 * here to Core is same-site and carries it. The flow:
 *
 * 1. The Console's sign-in page signs the person in (password, MFA) and sends them back.
 * 2. `fetchCoreToken` asks Core for a short token for audience `pms` — Core only answers this
 *    app's registered origin — plus the organisations where the person holds a PMS role.
 * 3. `exchangeCoreToken` trades it for an ordinary PMS session in the chosen organisation.
 *
 * There is no PMS refresh cookie for such a session: when the PMS token lapses, the restore
 * path (`restoreSession`) simply repeats 2–3, so signing out of Core ends access here too.
 *
 * Hidden entirely unless both `VITE_CORE_API_BASE_URL` and `VITE_CONSOLE_URL` are set.
 */

const CORE_API: string = import.meta.env["VITE_CORE_API_BASE_URL"] ?? "";
const CONSOLE: string = import.meta.env["VITE_CONSOLE_URL"] ?? "";
const MODE_KEY = "sarva.auth.mode";

export const coreSignInAvailable: boolean = CORE_API !== "" && CONSOLE !== "";

export interface CoreOrganisation {
  id: string;
  name: string;
  slug: string;
}

export interface CoreToken {
  accessToken: string;
  organisations: CoreOrganisation[];
}

/** Where to send the person to sign in, and where the Console sends them back. */
export function coreSignInUrl(): string {
  const back = `${window.location.origin}/sign-in/sarva`;
  return `${CONSOLE}/sign-in?return_to=${encodeURIComponent(back)}`;
}

export function signedInWithCore(): boolean {
  try {
    return window.localStorage.getItem(MODE_KEY) === "core";
  } catch {
    return false;
  }
}

function rememberCoreMode(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(MODE_KEY, "core");
    else window.localStorage.removeItem(MODE_KEY);
  } catch {
    // Private windows can refuse storage. The cost is a re-sign-in on reload, never a wrong one.
  }
}

/** A PMS-audience token from Core, or `null` when there is no live Core session. */
export async function fetchCoreToken(): Promise<CoreToken | null> {
  if (!coreSignInAvailable) return null;
  try {
    const response = await fetch(`${CORE_API}/api/v1/auth/token`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ audience: "pms" }),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as {
      access_token: string;
      organisations?: CoreOrganisation[];
    };
    return { accessToken: body.access_token, organisations: body.organisations ?? [] };
  } catch {
    return null;
  }
}

/** Trade a Core token for a PMS session in one organisation (Core organisation = PMS tenant). */
export async function exchangeCoreToken(
  tenantId: string,
  coreAccessToken: string,
): Promise<boolean> {
  try {
    const envelope = await api.exchangeCoreToken({
      body: { tenant_id: tenantId },
      headers: { Authorization: `Bearer ${coreAccessToken}` },
    });
    const token = envelope.data;
    rememberTenantId(token.tenant_id);
    rememberCoreMode(true);
    // (H) doc 23 §11a: in memory and nowhere else.
    storeCredentials({
      accessToken: token.access_token,
      expiresAt: token.expires_at,
      actorId: token.actor_id,
      tenantId: token.tenant_id,
      sessionId: token.session_id,
      email: null,
    });
    return true;
  } catch {
    return false;
  }
}

/** The restore path for a Core session: a fresh Core token, exchanged for the same tenant. */
export async function restoreCoreSession(tenantId: string): Promise<boolean> {
  if (tenantId === "") return false;
  const core = await fetchCoreToken();
  if (core === null || !core.organisations.some((o) => o.id === tenantId)) return false;
  return exchangeCoreToken(tenantId, core.accessToken);
}

/** Sign out of Core too, so the next restore does not silently sign straight back in. */
export async function endCoreSession(): Promise<void> {
  rememberCoreMode(false);
  if (!coreSignInAvailable) return;
  try {
    await fetch(`${CORE_API}/api/v1/auth/sign-out`, { method: "POST", credentials: "include" });
  } catch {
    // As with the PMS sign-out: the local credential is dropped either way.
  }
}
